import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { GET_PARTICIPANT_CASE } from "../../apis/queries/getParticipantCase";
import { GET_AGENCY } from "../../apis/queries/getAgency";
import { pendoConfig } from "../../config";
import type { PendoIdentity } from "./pendoTypes";
import { activeLocale } from "../../resources/strings";

/**
 * Queue-stub Pendo install snippet. Creates the `window.pendo` stub that buffers
 * calls made before the agent finishes loading, then injects the agent script
 * for the configured API key and invokes `onLoad` once it is ready.
 */
const installPendoScript = (apiKey: string, onLoad: () => void): void => {
  (function (p, e, n, d) {
    const o = ((p as unknown as Record<string, any>)[d] =
      (p as unknown as Record<string, any>)[d] || {});
    o._q = o._q || [];
    const v = [
      "initialize",
      "identify",
      "updateOptions",
      "pageLoad",
      "track",
      "trackAgent",
      // clearSession is called on logout; stub it so it's queued safely if the
      // agent hasn't finished loading yet.
      "clearSession",
    ];
    for (let w = 0, x = v.length; w < x; ++w)
      (function (m) {
        o[m] =
          o[m] ||
          function () {
            o._q[m === v[0] ? "unshift" : "push"](
              [m].concat([].slice.call(arguments, 0)),
            );
          };
      })(v[w]);
    const y = e.createElement(n) as HTMLScriptElement;
    y.async = true;
    y.src = `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;
    y.onload = () => onLoad();
    y.onerror = () =>
      console.error("Pendo agent failed to load; analytics disabled.");
    const z = e.getElementsByTagName(n)[0];
    if (z?.parentNode) {
      z.parentNode.insertBefore(y, z);
    } else {
      // No existing <script> (tests / embedded renders): fall back to <head>.
      (e.head ?? e.documentElement).appendChild(y);
    }
  })(window, document, "script", "pendo");
};

/**
 * Injects the Pendo agent the first time the app loads (when an API key is
 * configured) and reports when it is ready. No-op when the key is empty, so
 * local/dev environments without a Pendo key behave normally.
 */
const usePendoLoaded = (): boolean => {
  const [pendoLoaded, setPendoLoaded] = useState(
    () => window.pendo !== undefined,
  );

  useEffect(() => {
    // `window.pendo` is set synchronously by installPendoScript, so this also
    // guards against a second injection when the effect re-runs before the
    // agent finishes loading (e.g. StrictMode's double-invoke in development).
    if (pendoLoaded || window.pendo || !pendoConfig.apiKey) {
      return;
    }
    installPendoScript(pendoConfig.apiKey, () => setPendoLoaded(true));
  }, [pendoLoaded]);

  return pendoLoaded;
};

/**
 * Builds the Pendo visitor/account identity from the authenticated participant
 * and their agency. Returns null until the participant is known (and when the
 * user is signed out) so the caller can clear the Pendo session.
 */
const usePendoIdentity = (): PendoIdentity | null => {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const authenticated = authStatus === "authenticated";

  const { data: caseData } = useQuery(GET_PARTICIPANT_CASE, {
    skip: !authenticated,
  });
  const { data: agencyData } = useQuery(GET_AGENCY, { skip: !authenticated });

  const participant = caseData?.getParticipantCase?.participant;
  const agency = agencyData?.getAgency;

  const participantId = participant?.id;
  const agencyId = agency?.id;
  const agencyName = agency?.name;

  return useMemo(() => {
    if (!authenticated || !participantId) {
      return null;
    }
    return {
      // Send only the participant id (our own lookup key) — no name/PII.
      // Agency id + name are org-level data, not personal.
      // `locale` is the coarse resolved UI locale ("en"/"es"), not the raw
      // browser tag — enough to measure Spanish adoption without
      // adding a finer-grained signal about the person.
      visitor: {
        id: participantId,
        locale: activeLocale,
      },
      ...(agencyId
        ? { account: { id: agencyId, agency: agencyName } }
        : {}),
    };
  }, [authenticated, participantId, agencyId, agencyName]);
};

/**
 * Drives the Pendo lifecycle: initialize once when the agent is loaded and an
 * identity is available, re-identify on subsequent identity changes, and clear
 * the session on logout.
 */
const usePendo = (): void => {
  const pendoLoaded = usePendoLoaded();
  const pendoIdentity = usePendoIdentity();
  const initialized = useRef(false);

  useEffect(() => {
    if (!pendoLoaded || !window.pendo) {
      return;
    }
    const { pendo } = window;
    if (pendoIdentity) {
      if (!initialized.current) {
        pendo.initialize(pendoIdentity);
        initialized.current = true;
      } else {
        pendo.identify(pendoIdentity);
      }
    } else if (initialized.current) {
      // User logged out: clear the session so later events aren't attributed.
      pendo.clearSession();
      initialized.current = false;
    }
  }, [pendoLoaded, pendoIdentity]);
};

/**
 * Headless component that wires up Pendo analytics. Renders nothing; mount it
 * once inside the authenticated provider tree (Apollo + Amplify Authenticator).
 */
const Pendo = (): null => {
  usePendo();
  return null;
};

export default Pendo;
